import React from "react";
import { motion } from "framer-motion";
import { Baby, Check, Gift, Info, Layers, Scale } from "lucide-react";
import { imgUrl } from "@/config";
import { computeRentalPrice } from "@/utils/pricing";
import { disneyApproved } from "@/utils/strollerAttrs";
import { trackEvent } from "@/utils/analytics";

export const ProductCard = ({ p, onSelect, selectedQty, range, compact = false, onOpenDetails, onToggleCompare, comparing = false, soldOut = false, availableCount = null, onRequest }) => {
  const pricing = computeRentalPrice(p, range);
  const comparable = !!(p.stroller_attributes?.include_in_comparison && onToggleCompare);

  // Compact mode powers the dense 4-across catalog grid. Tier pricing chart
  // stays visible (the user asked for it back) at the cost of feature bullets —
  // the full features + description live in the ProductDetailsModal opened on
  // hover-click. Trades depth for density so customers see more inventory.
  const pad = compact ? "p-3" : "p-6";
  const titleCls = compact ? "font-serif text-base text-[#F6F1E4] leading-tight line-clamp-2" : "font-serif text-2xl leading-tight text-[#F6F1E4]";
  const priceChipCls = compact ? "text-lg" : "text-xl";

  const openDetails = (e) => {
    // Never intercept clicks on the "Add" button or other interactive controls.
    if (e.target.closest("button")) return;
    onOpenDetails?.(p);
  };

  const CardBody = (
    <motion.div
      whileHover={{ y: compact ? -4 : -8 }}
      transition={{ type: "spring", stiffness: 200 }}
      className={`product-card group ${compact && onOpenDetails ? "cursor-pointer" : ""} ${soldOut ? "opacity-70 saturate-50" : ""}`}
      onClick={compact && onOpenDetails ? openDetails : undefined}
      data-testid={`product-card-${p.id}`}
      data-sold-out={soldOut ? "true" : undefined}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={imgUrl(p.image)} alt={p.name} className={`w-full h-full object-contain p-2 bg-black/20 transition-transform duration-700 group-hover:scale-105 ${soldOut ? "grayscale" : ""}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#040A0F] via-transparent to-transparent pointer-events-none" />
        {soldOut && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid={`sold-out-ribbon-${p.id}`}>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-[18deg] w-[160%] bg-[#0A2438]/95 border-y-2 border-[#F4A93A] text-center py-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.6)]">
              <span className={`${compact ? "text-xs" : "text-sm"} font-bold uppercase tracking-[0.3em] text-[#F4A93A]`}>Out of stock</span>
            </div>
          </div>
        )}

        {/* Enchanted Peace of Mind teaser — subtle top-left pill so shoppers
            know they can cancel-anytime BEFORE they hit checkout. Clicking
            opens ProductDetailsModal scrolled to the cancellation section. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); trackEvent("pom_ribbon_click", { productId: p.id }); onOpenDetails?.(p, { scrollTo: "cancellation" }); }}
          className={`absolute ${compact ? "top-1.5 left-1.5" : "top-3 left-3"} rounded-full bg-[#0A2438]/85 backdrop-blur-sm border border-[#FFD166]/40 shadow-black/40 shadow-sm px-2 py-0.5 flex items-center gap-1 hover:bg-[#FFD166]/15 hover:border-[#FFD166]/70 transition-colors`}
          title="Click for cancellation details · Enchanted Peace of Mind"
          data-testid={`pom-ribbon-${p.id}`}
        >
          <span className="text-[10px]" aria-hidden>✨</span>
        </button>

        <div className={`absolute ${compact ? "top-1.5 right-1.5" : "top-3 right-3"} flex flex-col items-end gap-1`} data-testid={`price-badge-${p.id}`}>
          <span className="rounded-full bg-[#0A2438]/95 border border-[#FFD166]/50 shadow-lg shadow-black/40 px-2 py-0.5 flex items-baseline gap-1">
            <span className="text-[9px] uppercase tracking-wider text-[#B9C8D5]">from</span>
            <span className={`text-[#FFD166] font-serif ${priceChipCls} leading-none`}>${(p.tier_8_plus ?? p.price_per_day).toFixed(0)}</span>
            <span className="text-[9px] text-[#B9C8D5]">/day</span>
          </span>
          {!compact && (
            <span className="rounded-full bg-[#F4A93A]/90 text-[#0A2438] text-[9px] font-bold px-2 py-0.5 tracking-wide">3-DAY MIN</span>
          )}
        </div>

        {/* Hover reveal — a subtle "click for details" nudge that fades in on the image */}
        {compact && onOpenDetails && (
          <div
            className="absolute inset-x-0 bottom-0 py-2 px-3 bg-gradient-to-t from-[#040A0F] via-[#040A0F]/90 to-transparent text-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
            data-testid={`hover-details-hint-${p.id}`}
          >
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#FFD166] inline-flex items-center gap-1">
              <Info className="w-3 h-3" /> Click for details
            </span>
          </div>
        )}

        {/* Scarcity badge — shown when only 1 or 2 units are left for selected dates */}
        {!soldOut && availableCount !== null && availableCount > 0 && availableCount <= 2 && (
          <div
            className={`absolute bottom-2 left-2 flex items-center gap-1 rounded-full px-2 py-0.5 border shadow-black/40 shadow-sm animate-pulse-slow ${
              availableCount === 1
                ? "bg-red-900/90 border-red-400/60 text-red-200"
                : "bg-amber-900/90 border-amber-400/50 text-amber-200"
            }`}
            data-testid={`scarcity-badge-${p.id}`}
          >
            <span className={compact ? "text-[9px]" : "text-[10px]"} aria-label={`Only ${availableCount} left`}>
              {availableCount === 1 ? "Last one!" : `Only ${availableCount} left`}
            </span>
          </div>
        )}
      </div>
      <div className={`${pad} relative`}>
        <h3 className={titleCls}>{p.name}</h3>
        {!compact && p.tagline && <p className="text-[#B9C8D5] mt-1 text-sm italic">{p.tagline}</p>}

        {!compact && (
          <ul className="mt-4 space-y-1.5">
            {(p.features || []).slice(0, 3).map((f) => (
              <li key={f} className="text-sm text-[#F6F1E4]/90 flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-[#FFD166]" /> {f}
              </li>
            ))}
          </ul>
        )}

        {p.is_bundle && (
          <div className={`${compact ? "mt-2 text-[10px]" : "mt-3 text-[11px]"} inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#FFD166]/40 text-[#FFD166] bg-[#FFD166]/10`} data-testid={`bundle-badge-${p.id}`}>
            <Layers className="w-3 h-3" /> Bundle
          </div>
        )}

        {disneyApproved(p.stroller_attributes) === true && (
          <div
            className={`${compact ? "mt-1.5 text-[9px]" : "mt-2 text-[10px]"} inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-400/40 text-emerald-300 bg-emerald-500/10`}
            data-testid={`disney-approved-badge-${p.id}`}
            title="Unfolded size fits Disney's 31″ wide × 52″ long stroller limit"
          >
            Disney approved ✓
          </div>
        )}

        {/* Tier pricing chart — shown in BOTH compact and full modes so customers
            see the length-of-stay discount even in the 4-across grid. */}
        <div
          className={`${compact ? "mt-2 p-2" : "mt-4 p-3"} rounded-lg border border-[#FFD166]/20 bg-black/25 grid grid-cols-3 gap-1.5 text-center`}
          data-testid={`tier-pricing-${p.id}`}
        >
          <div>
            <div className={`${compact ? "text-[8px]" : "text-[10px]"} uppercase tracking-wider text-[#B9C8D5]`}>1-3d</div>
            <div className={`font-serif ${compact ? "text-sm" : "text-lg"} text-[#FFD166] leading-tight`}>
              ${(p.tier_1_3 ?? p.price_per_day).toFixed(0)}<span className={`${compact ? "text-[8px]" : "text-[10px]"} text-[#B9C8D5]`}>/d</span>
            </div>
          </div>
          <div className="border-x border-[#FFD166]/15">
            <div className={`${compact ? "text-[8px]" : "text-[10px]"} uppercase tracking-wider text-[#B9C8D5]`}>4-7d</div>
            <div className={`font-serif ${compact ? "text-sm" : "text-lg"} text-[#FFD166] leading-tight`}>
              ${(p.tier_4_7 ?? p.price_per_day).toFixed(0)}<span className={`${compact ? "text-[8px]" : "text-[10px]"} text-[#B9C8D5]`}>/d</span>
            </div>
          </div>
          <div>
            <div className={`${compact ? "text-[8px]" : "text-[10px]"} uppercase tracking-wider text-[#B9C8D5]`}>8+d</div>
            <div className={`font-serif ${compact ? "text-sm" : "text-lg"} text-[#FFD166] leading-tight`}>
              ${(p.tier_8_plus ?? p.price_per_day).toFixed(0)}<span className={`${compact ? "text-[8px]" : "text-[10px]"} text-[#B9C8D5]`}>/d</span>
            </div>
          </div>
        </div>

        {pricing ? (
          <div
            className={`${compact ? "mt-2 px-2 py-1.5" : "mt-3 px-3 py-2.5"} rounded-lg border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-between`}
            data-testid={`total-for-dates-${p.id}`}
          >
            <div className={`${compact ? "text-[10px]" : "text-[11px]"} leading-tight`}>
              <div className="uppercase tracking-wider text-emerald-300/85">Your total</div>
              <div className="text-[#B9C8D5]">
                {pricing.billableDays}d
                {pricing.isMinApplied && <span className="text-[#F4A93A]"> (3-day min)</span>}
              </div>
            </div>
            <div className={`font-serif ${compact ? "text-lg" : "text-2xl"} text-emerald-300 leading-none`}>
              ${pricing.total.toFixed(0)}
            </div>
          </div>
        ) : (
          <div className={`${compact ? "mt-2 px-2 py-1.5 text-[10px]" : "mt-3 px-3 py-2 text-[11px]"} rounded-lg border border-dashed border-[#FFD166]/30 bg-[#FFD166]/[0.04] text-[#B9C8D5]`} data-testid={`total-hint-${p.id}`}>
            <a href="#trip-setup" className="text-[#FFD166] hover:underline">Pick dates</a> to see total.
          </div>
        )}
        {(p.free_accessories || []).length > 0 && (
          <div
            className={`${compact ? "mt-2 px-2 py-1.5" : "mt-3 px-3 py-2"} rounded-lg border border-[#FFD166]/35 bg-gradient-to-r from-[#FFD166]/12 to-transparent`}
            data-testid={`free-included-${p.id}`}
          >
            <div className={`${compact ? "text-[8px]" : "text-[10px]"} uppercase tracking-[0.18em] text-[#FFD166] font-semibold flex items-center gap-1`}>
              <Gift className="w-3 h-3" /> Included free
            </div>
            <div className={`${compact ? "text-[10px]" : "text-xs"} text-[#F6F1E4]/90 mt-0.5 leading-snug`}>
              {(p.free_accessories || []).join(" · ")}
            </div>
          </div>
        )}
        <div className={`${compact ? "mt-3" : "mt-5"} flex items-center justify-between gap-2`}>
          {!compact && (
            <span className="text-xs text-[#B9C8D5] flex items-center gap-1"><Baby className="w-3.5 h-3.5" /> {p.min_age}</span>
          )}
          <div className={`flex items-center gap-1.5 ${compact ? "w-full" : ""}`}>
            {comparable && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleCompare(p); }}
                data-testid={`compare-toggle-${p.id}`}
                title={comparing ? "Remove from comparison" : "Compare this stroller"}
                aria-label={comparing ? "Remove from comparison" : "Compare this stroller"}
                className={`${compact ? "text-[10px] px-2 py-1.5" : "text-xs px-3 py-2"} shrink-0 rounded-full border transition-all inline-flex items-center gap-1 ${
                  comparing ? "bg-[#F4A93A] text-[#040A0F] border-[#F4A93A]" : "border-[#F4A93A]/45 text-[#F4A93A] hover:bg-[#F4A93A]/10"
                }`}
              >
                <Scale className="w-3 h-3" />{comparing ? "✓" : "Compare"}
              </button>
            )}
            {soldOut ? (
              <button
                onClick={(e) => { e.stopPropagation(); trackEvent("stock_request_click", { productId: p.id }); onRequest?.(p); }}
                data-testid={`request-stock-${p.id}`}
                className={`${compact ? "text-xs px-3 py-1.5 flex-1" : "text-sm px-4 py-2"} font-medium rounded-full border border-[#F4A93A] text-[#F4A93A] hover:bg-[#F4A93A]/15 transition-all`}
              >
                Request it
              </button>
            ) : (
            <button
              onClick={(e) => { e.stopPropagation(); trackEvent("product_add_click", { productId: p.id }); onSelect(p.id); }}
              data-testid={`add-to-cart-${p.id}`}
              className={`${compact ? "text-xs px-3 py-1.5 flex-1" : "text-sm px-4 py-2"} font-medium rounded-full border transition-all ${
                selectedQty ? "bg-[#FFD166] text-[#040A0F] border-[#FFD166]" : "border-[#FFD166]/35 text-[#FFD166] hover:bg-[#FFD166]/10"
              }`}
            >
              {selectedQty ? `× ${selectedQty} Added` : (compact ? "Add" : "Book Now")}
            </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  return CardBody;
};
